import * as React from 'react';
import { useSelector } from 'react-redux';
import { PublishTerms } from './PublishTerms';
import { withProvider } from '../../redux';
import {
  selectIsPromotionPackageSelected,
  selectIsSubagentLimitExceed,
  selectPublishTermsStatus,
  selectServiceIds,
} from '../../selectors/publishTerms';
import { EPublishTermsStatus } from '../../redux/reducers';
import { ApplicationContext } from '../ApplicationContext';
import { selectIsIdentificationNeeded, selectIsPublishToHiddenBase } from '../../selectors/publishAvailable';
import { ReactErrorLog } from '../common/ReactErrorLog';
import { IPublishTermsContainerProps } from './types';
import { selectIsAuctionEnabled } from '../../selectors/auction';
import { getPromotionSubheader } from './utils/getPromotionSubheader';
import { useIsMobileDevice } from '../../hooks';

export const Container = () => {
  const status = useSelector(selectPublishTermsStatus);
  const isSubagentLimitExceed = useSelector(selectIsSubagentLimitExceed);
  const isIdentificationNeeded = useSelector(selectIsIdentificationNeeded);
  const isPublishToHiddenBase = useSelector(selectIsPublishToHiddenBase);
  const serviceIds = useSelector(selectServiceIds);
  const isAuctionEnabled = useSelector(selectIsAuctionEnabled);
  const isPromotionPackageSelected = useSelector(selectIsPromotionPackageSelected);
  const isMobile = useIsMobileDevice();

  const promotionSubheader = getPromotionSubheader({ isAuctionEnabled, isPromotionPackageSelected, isMobile });

  const isInitial = status === EPublishTermsStatus.initial;
  const isLoading = status === EPublishTermsStatus.pending;
  const isSuccess = status === EPublishTermsStatus.success;
  const isError = status === EPublishTermsStatus.fail;
  const isServicesEmpty = isSuccess && serviceIds.length === 0 && !isSubagentLimitExceed;

  if (isInitial || isIdentificationNeeded || isPublishToHiddenBase || isServicesEmpty) {
    return null;
  }

  return (
    <PublishTerms
      isLoading={isLoading}
      isError={isError}
      isSubagentLimitExceed={isSubagentLimitExceed}
      promotionSubheader={promotionSubheader}
    />
  );
};

export const PublishTermsContainer = withProvider(
  ({ httpApi, logger, config, telemetry }: IPublishTermsContainerProps) => {
    const context = React.useMemo(() => ({ httpApi, logger, config, telemetry }), [httpApi, logger, config, telemetry]);

    const handleRenderSuccess = React.useCallback(() => {
      telemetry.increment('react.publish-terms.render.success');
    }, [telemetry]);

    const handleRenderError = React.useCallback(
      (error: Error, errorInfo: React.ErrorInfo) => {
        telemetry.increment('react.publish-terms.render.fail');

        logger.debug('Ошибка рендеринга услуг размещения', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
        });
      },
      [logger, telemetry],
    );

    return (
      <ApplicationContext.Provider value={context}>
        <ReactErrorLog onSuccess={handleRenderSuccess} onError={handleRenderError}>
          <Container />
        </ReactErrorLog>
      </ApplicationContext.Provider>
    );
  },
);
